import { useState } from "react";
import { Box } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import LoadingPage from "../LoadingPage";
import {
  BarcodeScanner,
  PhotoCapture,
  CheckinConfirmation,
  CheckinSuccess,
} from "../../components/admin/checkin/index";
import type { PatientInfo, CheckinData } from "../../types";

export default function AdminCheckinPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkinData, setCheckinData] = useState<CheckinData>({});

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setCheckinData({});
  };

  const updateCheckinData = (data: Partial<CheckinData>) => {
    setCheckinData((prev) => ({ ...prev, ...data }));
  };

  if (loading) {
    return <LoadingPage />;
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BarcodeScanner
            onScanSuccess={(barcode: string, patientInfo: PatientInfo) => {
              // Extract numeric booking ID
              console.debug('barcode', barcode);
              updateCheckinData({
                bookingId: patientInfo.booking_id,
                patientInfo,
                // Use image_barcode as photo instead of capturing
                photo: patientInfo.imageBarcode
              });
              // Skip photo capture step and go directly to confirmation
              setCurrentStep(1);
            }}
            onError={(error: string) => {
              console.error("Barcode scan error:", error);
            }}
          />
        );
      case 1:
        return (
          <PhotoCapture
            onPhotoCapture={(photo: string) => {
              updateCheckinData({ photo });
              handleNext();
            }}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <CheckinConfirmation
            checkinData={checkinData}
            onConfirm={() => {
              setLoading(true);
              // Simulate API call
              setTimeout(() => {
                setLoading(false);
                handleNext();
              }, 2000);
            }}
            onBack={() => {
              // Go back to barcode scanner (step 0)
              setCurrentStep(0);
            }}
          />
        );
      case 3:
        return (
          <CheckinSuccess
            checkinData={checkinData}
            onNewCheckin={handleReset}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box className="body-content" sx={{ minHeight: "calc(100vh - 72px)" }}>
      {/* Content */}
      <Box sx={{ flex: 1, p: 2 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            style={{ height: "100%" }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
}
